<template>
  <Page title="Company Settings" class="w-full">
    <TabSwitcher id="cs" :tabs="tabs" class="w-full" />
  </Page>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";

import Page from "@/components/Page.vue";
import TabSwitcher from "@/components/TabSwitcher/TabSwitcher.vue";
import AccountingClasses from "./AccountingClasses.vue";
import AccountingClassesMessage from "./AccountingClassesMessage.vue";
import AccountingSettings from "./AccountingSettings.vue";
import Departments from "./Departments.vue";
import FeatureSettings from "./FeatureSettings.vue";
import PricingSettings from "./PricingSettings.vue";

const store = useStore();
const props = withDefaults(
  defineProps<{
    defaultTaxability: string;
    defaultTaxZoneId: number;
    tenantId: number;
    salesTaxRate: string;
    financeMarkup: string;
    defaultMembershipProgramId: number;
    defaultQuoteTypeId: string;
    quoteTypes: any[];
    timezones: any[];
    preWorkEnabled: boolean;
    arrivalWindowDurationSeconds: number;
    timeCardsEnabled: boolean;
    dailyOvertimeHours: number;
    dailyDoubleOvertimeHours: number;
    jobStatusEnabled: boolean;
    pricingAdjustmentsEnabled: boolean;
    displayMembershipSavings?: boolean;
    displayMembershipSavingsAdHoc?: boolean;
    separateCommunicationOptOutEnabled?: boolean;
  }>(),
  {
    defaultTaxability: "nontaxable",
    arrivalWindowDurationSeconds: 3600,
    dailyOvertimeHours: 24,
    dailyDoubleOvertimeHours: 24,
    jobStatusEnabled: false,
    pricingAdjustmentsEnabled: false,
    displayMembershipSavings: true,
    displayMembershipSavingsAdHoc: false,
    separateCommunicationOptOutEnabled: false
  }
);

const settings = ref({
  pre_work_enabled: props.preWorkEnabled,
  arrival_window_duration_seconds: props.arrivalWindowDurationSeconds,
  time_cards_enabled: props.timeCardsEnabled,
  daily_overtime_hours: props.dailyOvertimeHours,
  daily_double_overtime_hours: props.dailyDoubleOvertimeHours,
  daily_overtime_enabled: props.dailyOvertimeHours !== 24,
  daily_double_overtime_enabled: props.dailyDoubleOvertimeHours !== 24,
  job_status_enabled: props.jobStatusEnabled,
  pricing_adjustments_enabled: props.pricingAdjustmentsEnabled,
  display_membership_savings: props.displayMembershipSavings,
  display_membership_savings_ad_hoc: props.displayMembershipSavingsAdHoc,
  separate_communication_opt_out_enabled:
    props.separateCommunicationOptOutEnabled
});

const showAccountingClasses = computed(
  () => store.getters.showAccountingClasses
);
const accountingProcessor = computed(() => store.getters.accountingProcessor);

const tabs = computed(() => {
  const accountingClasses = {
    id: "accountingClasses",
    name: "Accounting Settings"
  };
  if (accountingProcessor.value === "saasant") {
    accountingClasses["component"] = AccountingSettings;
  } else if (accountingProcessor.value === "qbo") {
    accountingClasses["component"] = showAccountingClasses.value
      ? AccountingClasses
      : AccountingClassesMessage;
  }

  return [
    {
      id: "departments",
      name: "Departments",
      component: Departments
    },
    {
      id: "pricing",
      name: "Pricing/Finance",
      component: PricingSettings,
      props: {
        tenantId: props.tenantId,
        salesTaxRate: props.salesTaxRate,
        financeMarkup: props.financeMarkup,
        selectedPricingPlanId: props.defaultMembershipProgramId,
        selectedQuoteStyleId: props.defaultQuoteTypeId,
        quotes: props.quoteTypes,
        defaultTaxability: props.defaultTaxability,
        defaultTaxZoneId: props.defaultTaxZoneId,
        pricingAdjustmentsEnabled: props.pricingAdjustmentsEnabled,
        displayMembershipSavings: props.displayMembershipSavings,
        displayMembershipSavingsAdHoc: props.displayMembershipSavingsAdHoc
      }
    },
    accountingClasses,
    {
      id: "featureSettings",
      name: "Feature Settings",
      component: FeatureSettings,
      props: {
        featureSettings: settings.value,
        onUpdateSettings: updateSettings
      }
    }
  ];
});

onMounted(() => {
  loadTimezones();
});

const loadTimezones = () => {
  store.dispatch(
    "setTimezoneOptions",
    props.timezones.map((tz) => ({
      label: tz,
      value: tz
    }))
  );
};

const updateSettings = (val) => {
  settings.value = val;
};
</script>
